import { Table, TableColumnsType, Button, Space, Tag } from 'antd';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS, convertToGraphQLFilters } from './components/FilterArea';
import AddressLocationReviewDrawer from './components/AddressLocationReviewDrawer';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {
  GET_ADDRESS_LOCATION_REVIEW_LISTING,
  GetAddressLocationReviewListingResponse,
  AddressLocationReviewType,
} from './graphql/queries/getAddressLocationReviewListing';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { useAddressLocationReviewExport } from './hooks/useAddressLocationReviewExport';

// 可排序列的客户端比较器（注意：当前为客户端排序，仅对当前页的数据生效）
type SortableColumnKey = 'address' | 'streetName' | 'createdAt';

const SORT_COMPARATORS: Record<
  SortableColumnKey,
  (a: AddressLocationReviewType, b: AddressLocationReviewType) => number
> = {
  address: (a, b) => (a.address || '').localeCompare(b.address || ''),
  streetName: (a, b) => (a.streetName || '').localeCompare(b.streetName || ''),
  createdAt: (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
};

const LocationCorrectionPage: React.FC = () => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  // 客户端排序状态（受控），让表格展示顺序与“下一条待审核”取数顺序保持一致
  const [sortedInfo, setSortedInfo] = useState<{ columnKey?: SortableColumnKey; order?: 'ascend' | 'descend' }>({});

  const graphQLFilters = convertToGraphQLFilters(filterOptions);
  // 下载：按筛选区当前选项导出 Excel，拿到预签名 URL 后直接触发浏览器下载
  const { exportByFilters, exporting } = useAddressLocationReviewExport();

  const { data, loading, refetch } = useQuery<GetAddressLocationReviewListingResponse>(
    GET_ADDRESS_LOCATION_REVIEW_LISTING,
    {
      variables: {
        filters: graphQLFilters,
        pageRequest: {
          page: currentPage,
          size: pageSize,
          sortDirection: 'DESC',
        },
      },
      // 审核场景对一致性要求高：审核完要立刻看到列表最新状态，且数据量小（分页 10 条），
      // 不依赖 Apollo cache，每次都向后端要最新数据。
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    }
  );

  // 按当前受控排序计算展示顺序，与 antd 表格内部展示顺序一致（descend 取反比较器）
  const sortedContent = useMemo(() => {
    const content = data?.getAddressLocationReviewListing?.content ?? [];
    const { columnKey, order } = sortedInfo;
    if (!columnKey || !order) return content;
    const comparator = SORT_COMPARATORS[columnKey];
    return [...content].sort((a, b) => (order === 'descend' ? -comparator(a, b) : comparator(a, b)));
  }, [data, sortedInfo]);

  // 用 ref 持有展示顺序与当前审核 id，供审核完成回调读取最新值，避免闭包过期
  const displayedContentRef = useRef<AddressLocationReviewType[]>([]);
  const selectedReviewIdRef = useRef<string | null>(null);
  useEffect(() => {
    displayedContentRef.current = sortedContent;
  }, [sortedContent]);

  const openReview = (id: string) => {
    selectedReviewIdRef.current = id;
    setSelectedReviewId(id);
    setDrawerVisible(true);
  };

  // 审核通过/拒绝成功后：跳到排序后列表中当前行之后的下一条待审核；若没有则关闭抽屉并刷新
  const handleReviewed = useCallback(() => {
    const list = displayedContentRef.current;
    const currentIdx = list.findIndex((r) => r.id === selectedReviewIdRef.current);
    const next =
      currentIdx >= 0 ? list.slice(currentIdx + 1).find((r) => r.status === 'PENDING') : undefined;
    if (next) {
      selectedReviewIdRef.current = next.id;
      setSelectedReviewId(next.id);
    } else {
      selectedReviewIdRef.current = null;
      setSelectedReviewId(null);
      setDrawerVisible(false);
      refetch();
    }
  }, [refetch]);

  const handleFilter = (options: FilterOptions) => {
    setFilterOptions(options);
    setCurrentPage(1);
    refetch({
      filters: convertToGraphQLFilters(options),
      pageRequest: {
        page: 1,
        size: pageSize,
        sortDirection: 'DESC',
      },
    });
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
    refetch({
      filters: graphQLFilters,
        pageRequest: {
          page,
          size,
          sortDirection: 'DESC',
        },
    });
  };

  const getStatusInfo = (status: string): { text: string; color: string } => {
    switch (status) {
      case 'PENDING':
        return { text: t('mapTool.addressLibrary.locationCorrectionPage.status.pending') as string, color: 'orange' };
      case 'APPROVED':
        return { text: t('mapTool.addressLibrary.locationCorrectionPage.status.approved') as string, color: 'green' };
      case 'REJECTED':
        return { text: t('mapTool.addressLibrary.locationCorrectionPage.status.rejected') as string, color: 'red' };
      default:
        return { text: status, color: 'default' };
    }
  };

  const getReportSourceText = (reportSource: string): string => {
    switch (reportSource) {
      case 'DRIVER':
        return t('mapTool.addressLibrary.locationCorrectionPage.reportSource.driver') as string;
      case 'BACKEND':
        return t('mapTool.addressLibrary.locationCorrectionPage.reportSource.backend') as string;
      default:
        return reportSource || '-';
    }
  };

  const getSortOrder = (key: SortableColumnKey): 'ascend' | 'descend' | null =>
    sortedInfo.columnKey === key ? sortedInfo.order ?? null : null;

  const columns: TableColumnsType<AddressLocationReviewType> = [
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'left',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.trackingNumber'),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      ellipsis: true,
      width: 160,
      align: 'left',
      render: (text: string) => text || '-',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.address'),
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      width: 300,
      align: 'left',
      sorter: SORT_COMPARATORS.address,
      sortOrder: getSortOrder('address'),
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.streetName'),
      dataIndex: 'streetName',
      key: 'streetName',
      ellipsis: true,
      width: 180,
      align: 'left',
      sorter: SORT_COMPARATORS.streetName,
      sortOrder: getSortOrder('streetName'),
      render: (text: string) => text || '-',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.originalCoordinates'),
      key: 'originalCoordinates',
      width: 220,
      align: 'left',
      render: (_, record) => (
        <span>
          {record.latitude}, {record.longitude}
        </span>
      ),
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reportedCoordinates'),
      key: 'reportedCoordinates',
      width: 225,
      align: 'left',
      render: (_, record) => (
        <span>
          {record.reportedLatitude}, {record.reportedLongitude}
        </span>
      ),
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reporterAccount'),
      key: 'reporterAccount',
      width: 130,
      align: 'left',
      render: (_, record) => record.reporterAccount?.phone || '-',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reportSource'),
      dataIndex: 'reportSource',
      key: 'reportSource',
      width: 110,
      align: 'left',
      render: (reportSource: string) => getReportSourceText(reportSource),
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reportedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      align: 'left',
      sorter: SORT_COMPARATORS.createdAt,
      sortOrder: getSortOrder('createdAt'),
      render: (text: string) => {
        if (!text) return '-';
        return new Date(text).toLocaleString();
      },
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.reviewerAccount'),
      key: 'reviewerAccount',
      width: 120,
      align: 'left',
      // 审核人是后台员工，展示姓名；未审核或审核侧同步的记录没有审核人账号，展示 -
      render: (_, record) => record.reviewerAccount?.name || '-',
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'left',
      render: (status: string) => {
        const statusInfo = getStatusInfo(status);
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: t('mapTool.addressLibrary.locationCorrectionPage.table.columns.action'),
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      align: 'left',
      render: (_, record) => (
        <Space size="middle">
          {record.status === 'PENDING' ? (
          <HasResource resourceCodes={[ResourceCode.locationCorrection.REVIEW_SUBMIT]}>
            <Button
              type="link"
              size="small"
              onClick={() => openReview(record.id)}
            >
              {t('mapTool.addressLibrary.locationCorrectionPage.actions.review')}
            </Button>
          </HasResource>
          ) : (
            <Button
              type="link"
              size="small"
              onClick={() => openReview(record.id)}
            >
              {t('mapTool.addressLibrary.locationCorrectionPage.actions.view')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const pageData = data?.getAddressLocationReviewListing;

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      {/* 下载用页面级已提交的 filterOptions（与列表同一份条件），不用 FilterArea 内部未提交的 state，与仓库其它列表页导出惯例一致 */}
      <FilterArea filters={filterOptions} handleFilter={handleFilter} handleExport={() => exportByFilters(filterOptions)} exporting={exporting} />

        <Table
          columns={columns}
          dataSource={sortedContent}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={false}
          onChange={(_pagination, _filters, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const key = s?.columnKey as SortableColumnKey | undefined;
            const order = s?.order ?? undefined;
            setSortedInfo(key && order ? { columnKey: key, order } : {});
          }}
        size="middle"
         className="mb-4"/>

        {/* 分页 */}
        <TablePaginationBar
          total={pageData?.totalElements || 0}
          current={currentPage}
          pageSize={pageSize}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          onShowSizeChange={(_, size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showQuickJumper={false}
        />

      <AddressLocationReviewDrawer
        visible={drawerVisible}
        onClose={() => {
          selectedReviewIdRef.current = null;
          setDrawerVisible(false);
          setSelectedReviewId(null);
        }}
        reviewId={selectedReviewId}
        onRefresh={() => {
          refetch();
        }}
        onReviewed={handleReviewed}
      />
    </div>
  );
};

export default LocationCorrectionPage;

